import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Radar, Users, User, Shield, LogOut, Satellite, MessageSquare, Target } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLiveEvents } from "@/lib/liveEvents";

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { unread, goalsDue } = useLiveEvents();
  const items = [
    { to: "/radar", label: "Radar", icon: Radar, testid: "nav-radar" },
    { to: "/groups", label: "Groups", icon: Users, testid: "nav-groups" },
    { to: "/threads", label: "Threads", icon: MessageSquare, testid: "nav-threads", badge: unread },
    { to: "/goals", label: "Goals", icon: Target, testid: "nav-goals", badge: goalsDue },
    { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
  ];
  if (user?.is_admin) items.push({ to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" });

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex flex-col">
      <header className="sticky top-0 z-40 glass border-b border-white/10 px-4 md:px-8 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Satellite size={16} className="text-[#00E5FF]" />
          <span className="font-[Unbounded] font-bold tracking-tighter text-sm">SIGNAL·RADAR</span>
        </div>
        <nav className="hidden md:flex items-center gap-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={({ isActive }) =>
                `relative px-4 py-2 rounded-full text-sm inline-flex items-center gap-2 transition-all ${
                  isActive ? "bg-[#00E5FF] text-black font-semibold" : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <it.icon size={14} />
              {it.label}
              {it.badge > 0 && (
                <span data-testid={`${it.testid}-badge`} className="ml-1 bg-[#FF007F] text-white text-[10px] font-mono px-1.5 min-w-[18px] h-[18px] rounded-full inline-flex items-center justify-center">{it.badge > 9 ? "9+" : it.badge}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); nav("/"); }}
          className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono text-white/60 hover:text-[#FF007F] transition-all"
        >
          <LogOut size={14} /> SIGN OUT
        </button>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden sticky bottom-0 z-40 glass border-t border-white/10 grid"
        style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
      >
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`m-${it.testid}`}
            className={({ isActive }) =>
              `relative flex flex-col items-center gap-1 py-3 text-[10px] font-mono tracking-wider ${
                isActive ? "text-[#00E5FF]" : "text-white/50"
              }`
            }
          >
            <it.icon size={18} />
            {it.label.toUpperCase()}
            {it.badge > 0 && (
              <span className="absolute top-1 right-1/4 bg-[#FF007F] text-white text-[9px] font-mono px-1 min-w-[14px] h-[14px] rounded-full inline-flex items-center justify-center">{it.badge > 9 ? "9+" : it.badge}</span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
